import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Check token expiration and auto-logout
  const checkTokenExpiration = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;

    try {
      const decoded = jwtDecode(storedToken);
      const currentTime = Date.now() / 1000;

      // If token is expired, logout
      if (decoded.exp <= currentTime) {
        console.log('🔒 Session expirée, déconnexion automatique');
        logout();
        return false;
      }

      // If token expires in less than 5 minutes, show a warning
      const timeUntilExpiry = (decoded.exp - currentTime) * 1000; // in ms
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.warn('⚠️ Session expire bientôt');
      }

      return true;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      logout();
      return false;
    }
  }, [logout]);

  useEffect(() => {
    // Check for existing token on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const decoded = jwtDecode(storedToken);
        const currentTime = Date.now() / 1000;

        if (decoded.exp > currentTime) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } else {
          // Token expired
          logout();
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout();
      }
    }

    setLoading(false);
  }, [logout]);

  // Set up interval to check token expiration every minute
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check immediately
    checkTokenExpiration();

    // Then check every minute
    const interval = setInterval(() => {
      checkTokenExpiration();
    }, 60 * 1000); // Every 60 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, checkTokenExpiration]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      updateUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      // If API returns 401, token is invalid, logout
      if (error.response?.status === 401) {
        logout();
      }
      // If API returns 429, don't throw - just return current user data
      // This prevents retry loops
      if (error.response?.status === 429) {
        console.warn('Rate limit reached for /api/auth/me, using cached user data');
        return user;
      }
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};



